import React, {
  useEffect,
  useState
} from 'react';

import {
  FaSearch,
  FaEye,
  FaDownload,
  FaWallet,
  FaTimes,
  FaChartLine,
  FaCoins,
  FaClock,
  FaCheckCircle,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
  FaCopy,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaGlobeAmericas
} from 'react-icons/fa';

import {
  MapContainer,
  TileLayer,
  Marker,
  Popup
} from 'react-leaflet';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import {
  getTrades,
  exportTrades
} from '../services/tradeService';

import { initAuth } from '../utils/auth';
import {
  parseLocation,
  getValidLocations,
  formatLocationText
} from '../utils/locationParser';

import './Deposits.css';

// Fix for Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const Trades = () => {

  /*
  |--------------------------------------------------------------------------
  | STATES
  |--------------------------------------------------------------------------
  */
  const [loading, setLoading] = useState(false);
  const [trades, setTrades] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapMode, setMapMode] = useState('single'); // 'single' or 'all'
  const [selectedTradeForMap, setSelectedTradeForMap] = useState(null);

  /*
  |--------------------------------------------------------------------------
  | FETCH TRADES
  |--------------------------------------------------------------------------
  */
  const fetchTrades = async (currentPage = 1, searchValue = '') => {
    try {
      setLoading(true);

      const response = await getTrades(currentPage, searchValue, 10);

      console.log('Fetching page:', currentPage);
      console.log('Pagination response:', response.pagination);

      if (response.status) {
        setTrades(response.data || []);
        setAnalytics(response.analytics || {});
        setPagination(response.pagination || {
          current_page: currentPage,
          last_page: 1,
          per_page: 10,
          total: 0
        });
      }
    } catch (error) {
      console.error('Fetch trades error:', error);
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | INITIAL LOAD
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    // Initialize authentication on component mount
    initAuth();
    fetchTrades(page, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  /*
  |--------------------------------------------------------------------------
  | SEARCH
  |--------------------------------------------------------------------------
  */
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPage(1);
    fetchTrades(1, value);
  };

  /*
  |--------------------------------------------------------------------------
  | STATUS COLORS
  |--------------------------------------------------------------------------
  */
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'info';
      case 'inactive':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  /*
  |--------------------------------------------------------------------------
  | VIEW DETAILS
  |--------------------------------------------------------------------------
  */
  const handleViewDetails = (trade) => {
    setSelectedTrade(trade);
    setShowModal(true);
  };

  /*
  |--------------------------------------------------------------------------
  | CLOSE MODAL
  |--------------------------------------------------------------------------
  */
  const closeModal = () => {
    setShowModal(false);
    setSelectedTrade(null);
    setCopySuccess('');
  };

  /*
  |--------------------------------------------------------------------------
  | COPY TO CLIPBOARD
  |--------------------------------------------------------------------------
  */
  const copyToClipboard = (text, field) => {
    if (text && text !== 'N/A') {
      navigator.clipboard.writeText(text);
      setCopySuccess(field);
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | MAP HANDLERS
  |--------------------------------------------------------------------------
  */
  const handleViewOnMap = (trade) => {
    const location = parseLocation(trade.location);
    if (location && location.lat && location.lng) {
      setSelectedTradeForMap(trade);
      setMapMode('single');
      setShowMapModal(true);
    } else {
      console.warn('No valid location for trade:', trade.id);
    }
  };

  const handleViewAllOnMap = () => {
    const validLocations = getValidLocations(trades);
    if (validLocations.length > 0) {
      setMapMode('all');
      setShowMapModal(true);
    } else {
      console.warn('No trades with valid locations');
    }
  };

  const closeMapModal = () => {
    setShowMapModal(false);
    setSelectedTradeForMap(null);
  };

  /*
  |--------------------------------------------------------------------------
  | FORMAT NUMBER
  |--------------------------------------------------------------------------
  */
  const formatNumber = (num) => {
    if (!num) return '0';
    return parseFloat(num).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    });
  };

  /*
  |--------------------------------------------------------------------------
  | PAGINATION
  |--------------------------------------------------------------------------
  */
  const renderPagination = () => {
    const { current_page = 1, last_page = 1 } = pagination;
    let pages = [];

    // Ensure we have valid page numbers
    if (!last_page || last_page <= 1) return null;

    // Calculate page range
    let startPage = Math.max(1, current_page - 2);
    let endPage = Math.min(last_page, current_page + 2);

    // Add first page
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          className="page-btn"
          onClick={() => setPage(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="dots1" className="page-dots">...</span>);
      }
    }

    // Add page range
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`page-btn ${i === page ? 'active' : ''}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }

    // Add last page
    if (endPage < last_page) {
      if (endPage < last_page - 1) {
        pages.push(<span key="dots2" className="page-dots">...</span>);
      }
      pages.push(
        <button
          key={last_page}
          className="page-btn"
          onClick={() => setPage(last_page)}
        >
          {last_page}
        </button>
      );
    }

    return pages;
  };

  return (
    <div className="deposits-page">

      {/* PAGE HEADER */}
      <div className="page-header">
        <h1 style={{ color: "white" }}>Trade Management</h1>
        <p style={{ color: "white" }} >Monitor and manage all trades transactions</p>
      </div>

      {/* ANALYTICS CARDS */}
      <div className="analytics-grid">
        <div className="analytics-card primary">
          <div className="card-icon">
            <FaWallet />
          </div>
          <div className="card-content">
            <h3>{analytics.total_stakes || 0}</h3>
            <p>Total Stakes</p>
          </div>
        </div>

        <div className="analytics-card success">
          <div className="card-icon">
            <FaCoins />
          </div>
          <div className="card-content">
            <h3>${formatNumber(analytics.total_stake_amount)}</h3>
            <p>Total Staked</p>
          </div>
        </div>

        <div className="analytics-card info">
          <div className="card-icon">
            <FaChartLine />
          </div>
          <div className="card-content">
            <h3>{formatNumber(analytics.total_income)} ZRX</h3>
            <p>Total Income</p>
          </div>
        </div>

        <div className="analytics-card warning">
          <div className="card-icon">
            <FaCheckCircle />
          </div>
          <div className="card-content">
            <h3>{analytics.active_stakes || 0}</h3>
            <p>Active Stakes</p>
          </div>
        </div>

        <div className="analytics-card secondary">
          <div className="card-icon">
            <FaClock />
          </div>
          <div className="card-content">
            <h3>{analytics.today_stakes || 0}</h3>
            <p>Today's Stakes</p>
          </div>
        </div>

        <div className="analytics-card danger">
          <div className="card-icon">
            <FaCoins />
          </div>
          <div className="card-content">
            <h3>${formatNumber(analytics.today_stake_amount)}</h3>
            <p>Today's Amount</p>
          </div>
        </div>

        {/* <div className="analytics-card purple">
          <div className="card-icon">
            <FaChartLine />
          </div>
          <div className="card-content">
            <h3>{formatNumber(analytics.total_direct_income)} ZRX</h3>
            <p>Direct Income</p>
          </div>
        </div>

        <div className="analytics-card teal">
          <div className="card-icon">
            <FaChartLine />
          </div>
          <div className="card-content">
            <h3>{formatNumber(analytics.total_level_income)} ZRX</h3>
            <p>Level Income</p>
          </div>
        </div> */}
      </div>

      {/* CONTROLS */}
      <div className="table-controls">
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by ID, wallet, package, or transaction hash..."
            className="search-input"
            value={search}
            onChange={handleSearch}
          />
        </div>

        <div className="controls-right">
          <button
            className="btn-map"
            onClick={handleViewAllOnMap}
            title="View all trades on map"
          >
            <FaGlobeAmericas /> View All on Map
          </button>

          <button
            className="btn-export"
            onClick={() => exportTrades(trades)}
          >
            <FaDownload /> Export Data
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="table-container">
        {loading ? (
          <div className="loading-container">
            <FaSpinner className="spinner" />
            <p>Loading stakes...</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>User</th>
                <th>Wallet</th>
                <th>Package</th>
                <th>Amount</th>
                <th>Tokens</th>
                <th>Status</th>
                <th>Income</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {trades.length > 0 ? (
                trades.map((trade) => (
                  <tr key={trade.id} onClick={() => handleViewDetails(trade)}>
                    <td>
                      <span className="id-badge">#{trade.id}</span>
                    </td>
                    <td>
                      <div className="user-info">
                        <span className="user-id">{trade.user_id}</span>
                        {trade.user_name && trade.user_name !== 'N/A' && (
                          <span className="user-name">{trade.user_name}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="wallet-cell">
                        {trade.user_wallet && trade.user_wallet !== 'N/A'
                          ? `${trade.user_wallet.slice(0, 6)}...${trade.user_wallet.slice(-4)}`
                          : 'N/A'}
                      </div>
                    </td>
                    <td>
                      <span className="package-name">{trade.package_name || 'N/A'}</span>
                    </td>
                    <td>
                      <div className="amount-cell">
                        <span className="amount">${formatNumber(trade.stake_amount)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="token-info">
                        <span className="token-amount">{formatNumber(trade.token_amount)}</span>
                        <span className="token-name">{trade.token_name || 'ZRX'}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusColor(trade.stake_status)}`}>
                        {trade.stake_status || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <div className="income-cell">
                        <span className="income-total">${formatNumber(trade.total_income)}</span>
                      </div>
                    </td>
                    <td>
                      <span className="date-cell">
                        {new Date(trade.joined || trade.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="action-buttons">
                        <button
                          className="btn-view"
                          onClick={() => handleViewDetails(trade)}
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        {parseLocation(trade.location)?.lat && (
                          <button
                            className="btn-map-single"
                            onClick={() => handleViewOnMap(trade)}
                            title="View on Map"
                          >
                            <FaMapMarkerAlt />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="empty-state">
                    <div className="empty-content">
                      <FaWallet className="empty-icon" />
                      <p>No stakes found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* PAGINATION */}
      {pagination && pagination.last_page > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {((page - 1) * (pagination.per_page || 10)) + 1} to{' '}
            {Math.min(page * (pagination.per_page || 10), pagination.total || 0)} of{' '}
            {pagination.total || 0} entries
          </div>
          <div className="pagination-controls">
            <button
              className="page-nav-btn"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <FaChevronLeft />
            </button>

            <div className="page-numbers">
              {renderPagination()}
            </div>

            <button
              className="page-nav-btn"
              disabled={page >= pagination.last_page}
              onClick={() => setPage(page + 1)}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {showModal && selectedTrade && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Stake Details #{selectedTrade.id}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-grid">
                {/* User Information */}
                <div className="detail-section">
                  <h3>User Information</h3>
                  <div className="detail-row">
                    <span className="detail-label">User ID:</span>
                    <span className="detail-value">{selectedTrade.user_id}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">User Name:</span>
                    <span className="detail-value">{selectedTrade.user_name || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Wallet Address:</span>
                    <div className="detail-value with-action">
                      <span className="wallet-full">
                        {selectedTrade.user_wallet || 'N/A'}
                      </span>
                      {selectedTrade.user_wallet && selectedTrade.user_wallet !== 'N/A' && (
                        <button
                          className="copy-btn"
                          onClick={() => copyToClipboard(selectedTrade.user_wallet, 'wallet')}
                        >
                          {copySuccess === 'wallet' ? <FaCheckCircle /> : <FaCopy />}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Sponsor Wallet:</span>
                    <div className="detail-value with-action">
                      <span className="wallet-full">
                        {selectedTrade.sponsor_wallet || 'N/A'}
                      </span>
                      {selectedTrade.sponsor_wallet && selectedTrade.sponsor_wallet !== 'N/A' && (
                        <button
                          className="copy-btn"
                          onClick={() => copyToClipboard(selectedTrade.sponsor_wallet, 'sponsor')}
                        >
                          {copySuccess === 'sponsor' ? <FaCheckCircle /> : <FaCopy />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stake Information */}
                <div className="detail-section">
                  <h3>Stake Information</h3>
                  <div className="detail-row">
                    <span className="detail-label">Package:</span>
                    <span className="detail-value package-badge">
                      {selectedTrade.package_name || 'N/A'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Stake Amount:</span>
                    <span className="detail-value amount-large">
                      ${formatNumber(selectedTrade.stake_amount)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Token Amount:</span>
                    <span className="detail-value">
                      {formatNumber(selectedTrade.token_amount)} {selectedTrade.token_name || 'ZRX'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span className={`status-badge large ${getStatusColor(selectedTrade.stake_status)}`}>
                      {selectedTrade.stake_status || 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Income Details */}
                <div className="detail-section">
                  <h3>Income Details</h3>
                  <div className="detail-row">
                    <span className="detail-label">Total Income:</span>
                    <span className="detail-value amount-large success">
                      ${formatNumber(selectedTrade.total_income)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Direct Income:</span>
                    <span className="detail-value">
                      ${formatNumber(selectedTrade.direct_income)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Level Income:</span>
                    <span className="detail-value">
                      ${formatNumber(selectedTrade.level_income)}
                    </span>
                  </div>
                </div>

                {/* Transaction Details */}
                <div className="detail-section">
                  <h3>Transaction Details</h3>
                  <div className="detail-row">
                    <span className="detail-label">Transaction Hash:</span>
                    <div className="detail-value with-action">
                      <span className="txn-hash">
                        {selectedTrade.txn_hash || 'N/A'}
                      </span>
                      {selectedTrade.txn_hash && selectedTrade.txn_hash !== 'N/A' && (
                        <>
                          <button
                            className="copy-btn"
                            onClick={() => copyToClipboard(selectedTrade.txn_hash, 'txn')}
                          >
                            {copySuccess === 'txn' ? <FaCheckCircle /> : <FaCopy />}
                          </button>
                          <a
                            href={`https://etherscan.io/tx/${selectedTrade.txn_hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="external-link"
                          >
                            <FaExternalLinkAlt />
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Join Date:</span>
                    <span className="detail-value">
                      {new Date(selectedTrade.joined || selectedTrade.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MAP MODAL */}
      {showMapModal && (
        <div className="modal-overlay" onClick={closeMapModal}>
          <div className="map-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {mapMode === 'single'
                  ? `Stake Location - #${selectedTradeForMap?.id}`
                  : `All Stake Locations (${getValidLocations(trades).length} locations)`
                }
              </h2>
              <button className="modal-close" onClick={closeMapModal}>
                <FaTimes />
              </button>
            </div>

            <div className="map-modal-body">
              {mapMode === 'single' && selectedTradeForMap ? (
                // Single trade map view
                (() => {
                  const location = parseLocation(selectedTradeForMap.location);
                  if (!location || !location.lat || !location.lng) {
                    return <div className="map-error">No valid location data available</div>;
                  }

                  return (
                    <MapContainer
                      center={[location.lat, location.lng]}
                      zoom={13}
                      className="trade-map"
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <Marker position={[location.lat, location.lng]}>
                        <Popup>
                          <div className="map-popup">
                            <h4>Stake #{selectedTradeForMap.id}</h4>
                            <p><strong>User:</strong> {selectedTradeForMap.user_name || 'N/A'}</p>
                            <p><strong>Package:</strong> {selectedTradeForMap.package_name}</p>
                            <p><strong>Amount:</strong> ${formatNumber(selectedTradeForMap.stake_amount)}</p>
                            <p><strong>Status:</strong> {' '}
                              <span className={`status-badge ${getStatusColor(selectedTradeForMap.stake_status)}`}>
                                {selectedTradeForMap.stake_status}
                              </span>
                            </p>
                            <p><strong>Date:</strong> {new Date(selectedTradeForMap.joined).toLocaleDateString()}</p>
                            <p><strong>Location:</strong> {formatLocationText(location)}</p>
                          </div>
                        </Popup>
                      </Marker>
                    </MapContainer>
                  );
                })()
              ) : (
                // All trades map view
                (() => {
                  const validLocations = getValidLocations(trades);
                  if (validLocations.length === 0) {
                    return <div className="map-error">No trades with valid location data</div>;
                  }

                  // Calculate center from all locations
                  const bounds = validLocations.reduce((acc, trade) => {
                    return {
                      minLat: Math.min(acc.minLat, trade.parsedLocation.lat),
                      maxLat: Math.max(acc.maxLat, trade.parsedLocation.lat),
                      minLng: Math.min(acc.minLng, trade.parsedLocation.lng),
                      maxLng: Math.max(acc.maxLng, trade.parsedLocation.lng),
                    };
                  }, {
                    minLat: validLocations[0].parsedLocation.lat,
                    maxLat: validLocations[0].parsedLocation.lat,
                    minLng: validLocations[0].parsedLocation.lng,
                    maxLng: validLocations[0].parsedLocation.lng,
                  });

                  const centerLat = (bounds.minLat + bounds.maxLat) / 2;
                  const centerLng = (bounds.minLng + bounds.maxLng) / 2;

                  return (
                    <MapContainer
                      center={[centerLat, centerLng]}
                      zoom={5}
                      className="trade-map"
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      {validLocations.map((trade) => {
                        const customIcon = new L.Icon({
                          iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${
                            trade.stake_status === 'active' ? 'green' :
                            trade.stake_status === 'pending' ? 'orange' :
                            trade.stake_status === 'completed' ? 'blue' : 'grey'
                          }.png`,
                          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
                          iconSize: [25, 41],
                          iconAnchor: [12, 41],
                          popupAnchor: [1, -34],
                          shadowSize: [41, 41]
                        });

                        return (
                          <Marker
                            key={trade.id}
                            position={[trade.parsedLocation.lat, trade.parsedLocation.lng]}
                            icon={customIcon}
                          >
                            <Popup>
                              <div className="map-popup">
                                <h4>Stake #{trade.id}</h4>
                                <p><strong>User:</strong> {trade.user_name || 'N/A'}</p>
                                <p><strong>Package:</strong> {trade.package_name}</p>
                                <p><strong>Amount:</strong> ${formatNumber(trade.stake_amount)}</p>
                                <p><strong>Status:</strong> {' '}
                                  <span className={`status-badge ${getStatusColor(trade.stake_status)}`}>
                                    {trade.stake_status}
                                  </span>
                                </p>
                                <p><strong>Date:</strong> {new Date(trade.joined).toLocaleDateString()}</p>
                              </div>
                            </Popup>
                          </Marker>
                        );
                      })}
                    </MapContainer>
                  );
                })()
              )}

              {/* Map Legend */}
              <div className="map-legend">
                <h4>Status Legend:</h4>
                <div className="legend-items">
                  <div className="legend-item">
                    <span className="legend-color active"></span>
                    <span>Active</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color pending"></span>
                    <span>Pending</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color completed"></span>
                    <span>Completed</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color inactive"></span>
                    <span>Inactive</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trades;